
/**
 * Name matching that survives speech recognition.
 *
 * Voice arrives mangled: "pre ya" for Priya, "lark and lantern" for
 * "Lark & Lantern", "golf buddies" for "David's golf buddies". Every candidate
 * is scored on exact/prefix matches, a phonetic skeleton and character-trigram
 * similarity, per token and on the joined string, so split words still match.
 */

const STOPWORDS = new Set(
  (
    'a an the some any of for with and or to me my i id we our you please can could would like want get give ' +
    'have has is are it its that this those these do does anything something what whats which how also just maybe ' +
    'let lets from on in at s his her their'
  ).split(/\s+/),
);

export function normalize(text: string): string {
  return text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9\s']/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function tokenize(text: string): string[] {
  return normalize(text)
    .split(' ')
    .map((t) => t.replace(/'/g, ''))
    .filter((t) => t.length > 0 && !STOPWORDS.has(t));
}

/** Naive plural folding so "tacos" meets "taco". */
function stem(token: string): string {
  if (token.length > 4 && token.endsWith('ies')) return `${token.slice(0, -3)}y`;
  if (token.length > 4 && /(ches|shes|xes|ses)$/.test(token)) return token.slice(0, -2);
  if (token.length > 3 && token.endsWith('s') && !token.endsWith('ss')) return token.slice(0, -1);
  return token;
}

/**
 * Consonant skeleton tuned for English + Spanish menu words:
 * quesadilla -> "ksd", "key sadia" -> "ksd", horchata -> "hrxt", "hor chata" -> "hrxt".
 */
export function phoneticKey(word: string): string {
  let w = normalize(word).replace(/\s+/g, '');
  if (!w) return '';
  w = w
    .replace(/^kn/, 'n')
    .replace(/^wr/, 'r')
    .replace(/qu/g, 'k')
    .replace(/q/g, 'k')
    .replace(/ck/g, 'k')
    .replace(/ph/g, 'f')
    .replace(/gh/g, '')
    .replace(/(ch|sh|tch)/g, 'x')
    .replace(/c(?=[eiy])/g, 's')
    .replace(/c/g, 'k')
    .replace(/ll/g, 'y')
    .replace(/z/g, 's')
    .replace(/v/g, 'b')
    .replace(/dg/g, 'j')
    .replace(/([^s])h/g, '$1');
  // Keep the first letter, drop later vowels and glides, collapse repeats.
  return (w[0]! + w.slice(1).replace(/[aeiouyw]/g, '')).replace(/(.)\1+/g, '$1');
}

function trigrams(text: string): Set<string> {
  const padded = `  ${text} `;
  const grams = new Set<string>();
  for (let i = 0; i < padded.length - 2; i++) grams.add(padded.slice(i, i + 3));
  return grams;
}

/** Dice coefficient over character trigrams, 0..1. */
export function similarity(a: string, b: string): number {
  if (!a || !b) return 0;
  if (a === b) return 1;
  const ta = trigrams(a);
  const tb = trigrams(b);
  let shared = 0;
  for (const g of ta) if (tb.has(g)) shared++;
  return (2 * shared) / (ta.size + tb.size);
}

function tokenScore(q: string, d: string): number {
  if (q === d) return 1;
  const qs = stem(q);
  const ds = stem(d);
  if (qs === ds) return 0.95;
  if (qs.length >= 4 && ds.startsWith(qs)) return 0.8;
  if (ds.length >= 4 && qs.startsWith(ds)) return 0.7;
  const pq = phoneticKey(qs);
  const pd = phoneticKey(ds);
  const phon = pq.length >= 2 && pq === pd ? 0.72 : 0;
  const sim = similarity(qs, ds);
  return Math.max(phon, sim >= 0.45 ? sim * 0.75 : 0);
}

export type NameCandidate = { id: string; names: string[] };
export type NameMatch = { id: string; score: number; matched: string };

/** Score one spoken query against one name (0..1). */
export function nameScore(query: string, name: string): number {
  const q = tokenize(query);
  const n = tokenize(name);
  if (!q.length || !n.length) return 0;
  const joinedQ = q.join('');
  const joinedN = n.join('');
  if (joinedQ === joinedN) return 1;
  // Every query token must find a counterpart; average the best per token.
  let total = 0;
  for (const qt of q) {
    let best = 0;
    for (const nt of n) best = Math.max(best, tokenScore(qt, nt));
    total += best;
  }
  const perToken = total / q.length;
  const coverage = Math.min(1, q.length / n.length);
  const joined = Math.max(similarity(joinedQ, joinedN), phoneticKey(joinedQ) === phoneticKey(joinedN) && joinedQ.length > 3 ? 0.85 : 0);
  return Math.max(perToken * (0.75 + 0.25 * coverage), joined);
}

/** Best candidates for a spoken name, best first. Anything under `min` is dropped. */
export function matchName(query: string, candidates: NameCandidate[], min = 0.62): NameMatch[] {
  const out: NameMatch[] = [];
  for (const c of candidates) {
    let best = 0;
    let matched = '';
    for (const name of c.names) {
      const s = nameScore(query, name);
      if (s > best) {
        best = s;
        matched = name;
      }
    }
    if (best >= min) out.push({ id: c.id, score: Math.round(best * 1000) / 1000, matched });
  }
  return out.sort((a, b) => b.score - a.score);
}

/** Levenshtein distance, for short spoken names. */
export function editDistance(a: string, b: string): number {
  const row = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let prev = row[0]!;
    row[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cur = row[j]!;
      row[j] = Math.min(row[j]! + 1, row[j - 1]! + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1));
      prev = cur;
    }
  }
  return row[b.length]!;
}

/**
 * Sound-alike matching is right for venues and for garbled speech ("pre ya"),
 * but a single first name is usually a real, different person: "Jen" is not
 * June and "Tim" is not Tom. A one-word name matches a person only as one of
 * their names or aliases, or as a one-letter slip of a name of four letters or
 * more ("Pria").
 */
export function sameFirstName(query: string, names: string[]): boolean {
  const words = tokenize(query);
  if (words.length !== 1) return true;
  const q = words[0]!;
  const known = names.flatMap((n) => tokenize(n));
  return known.some((n) => n === q || stem(n) === stem(q) || (q.length >= 4 && n.length >= 4 && editDistance(q, n) <= 1));
}

/**
 * Pick from a small closed set (ten venues, a few circles) when the heard name is imperfect but one
 * candidate is clearly ahead: "O'Cross's Cantina" is Rosa's Cantina, "re-tamarant" is Green Tamarind.
 * A distinctive word counts on its own. Anything this resolves is read back by name before it
 * commits, so a wrong guess costs a "no", never a booking.
 */
export function matchClosedSet(query: string, candidates: NameCandidate[], opts: { min?: number; margin?: number } = {}): NameMatch | undefined {
  const exact = matchName(query, candidates)[0];
  if (exact) return exact;
  const min = opts.min ?? 0.4;
  const margin = opts.margin ?? 0.2;
  const q = tokenize(query).filter((t) => t.length >= 4);
  const scored = candidates
    .map((c) => {
      let best = 0;
      let matched = '';
      for (const name of c.names) {
        const words = tokenize(name).filter((t) => t.length >= 4);
        const distinctive = Math.max(0, ...q.flatMap((qt) => words.map((w) => tokenScore(qt, w))));
        const s = Math.max(nameScore(query, name), distinctive);
        if (s > best) {
          best = s;
          matched = name;
        }
      }
      return { id: c.id, score: Math.round(best * 1000) / 1000, matched };
    })
    .sort((a, b) => b.score - a.score);
  const [first, second] = scored;
  return first && first.score >= min && first.score - (second?.score ?? 0) >= margin ? first : undefined;
}
